import { Controller } from '@hotwired/stimulus';
import { dropdownMaxHeightPx } from '../../utils/dropdown_max_height';

export default class extends Controller {
  static targets = [
    'dropdown',
    'button',
    // search UI
    'list',
    'search',
    'clearSearchBtn',
    'limitError',
    'helper',
  ];
  static values = {
    // layout
    direction: { type: String, default: 'auto' }, // 'auto' | 'top' | 'bottom'
    minWidth: Number,
    maxWidth: Number,

    // chip classes (optional – can be injected from partial if needed)
    wrapperClass: String,
    avatarClass: String,
    labelClass: String,
    removeClass: String,
    avatarSizeClass: String,
    showAvatars: Boolean,
    closeIcon: { type: String, default: 'outline' }, // 'outline' | 'filled'
    maxVisibleChips: Number, // maximum number of chips to show before showing "+X" chip
    maxSelections: Number, // maximum number of items that can be selected

    // form association (needed when dropdown is teleported outside form)
    formId: String,
    fieldName: String,

    autoSubmit: Boolean,
    frontendValidation: Boolean,
  };

  isDropdownOpen = false;
  outsideBound = false;

  // === lifecycle ===
  connect() {
    this.dropdownEl = this.dropdownTarget;
    this.dropdownOriginParent = this.dropdownEl.parentElement;
    this.portalRoot = this._ensurePortalRoot();

    // relayout
    this._reposition = () => {
      if (this.isDropdownOpen) this._positionDropdown();
    };

    // outside/esc
    this._onDocPointerDown = (e) => {
      const t = e.target;
      const insideDropdown = this.dropdownEl.contains(t);
      const insideComponent = this.element.contains(t);
      if (!insideDropdown && !insideComponent) this.close();
    };
    this._onDocKeydown = (e) => {
      if (e.key === 'Escape') this.close();
    };

    this._onPreselect = (e) => {
      const { selectedIds } = e.detail || {};
      if (selectedIds && Array.isArray(selectedIds)) {
        this.preselectByIds(selectedIds);
      }
    };
    this.element.addEventListener('multi-select-chips-with-search:preselect', this._onPreselect);

    this._renderChipsFromDOM();

    const hasError = this.buttonTarget.dataset.hasError === 'true';
    if (hasError) this.showError();

    this._bindFormSubmitGuard();
  }

  disconnect() {
    this._unbindDropdownHandlers();
    this._teardownOutside();
    this._detachObservers();
    this._unbindFormSubmitGuard();
    this.element.removeEventListener('multi-select-chips-with-search:preselect', this._onPreselect);
    if (this.dropdownEl && this.dropdownEl.parentElement === this.portalRoot) {
      this.dropdownOriginParent.appendChild(this.dropdownEl);
    }
  }

  _bindFormSubmitGuard() {
    this.parentForm = this.element.closest('form');
    if (!this.parentForm) return;

    this._onFormSubmit = () => {
      if (this.dropdownEl && this.dropdownEl.parentElement === this.portalRoot) {
        this.dropdownOriginParent.appendChild(this.dropdownEl);
      }
    };
    this.parentForm.addEventListener('submit', this._onFormSubmit, true);

    this._onFormValidate = (event) => {
      if (this.frontendValidationValue && this.selectedValues.length === 0) {
        event.preventDefault();
        event.stopPropagation();
        this.showRequiredError();
        this.buttonTarget.focus();
      }
    };
    this.parentForm.addEventListener('submit', this._onFormValidate);
  }

  _unbindFormSubmitGuard() {
    if (this.parentForm && this._onFormSubmit) {
      this.parentForm.removeEventListener('submit', this._onFormSubmit, true);
    }
    if (this.parentForm && this._onFormValidate) {
      this.parentForm.removeEventListener('submit', this._onFormValidate);
    }
    this.parentForm = null;
    this._onFormSubmit = null;
    this._onFormValidate = null;
  }

  // === API ===
  toggle(event) {
    event?.preventDefault();
    event?.stopPropagation();
    this.isDropdownOpen ? this.close() : this.open();
  }

  open() {
    if (this.isDropdownOpen) return;

    // teleport
    this.portalRoot.appendChild(this.dropdownEl);

    this.dropdownEl.classList.remove('hidden');
    this.dropdownEl.classList.add('invisible', 'ss-transition-none', 'opacity-0', 'scale-95');

    // a11y
    this.buttonTarget.setAttribute('aria-expanded', 'true');

    this._positionDropdown();

    this._grabRefs();
    this._bindDropdownHandlers();
    this._enforceMaxSelections();

    if (this._emptyEl) this._emptyEl.hidden = true;
    if (this._searchEl) {
      this._searchEl.value = '';
      this._toggleClearBtn(false);
      this._filter('');
      try {
        this._searchEl.focus({ preventScroll: true });
      } catch (_) {}
    }

    void this.dropdownEl.offsetHeight;
    this.dropdownEl.classList.remove('ss-transition-none', 'invisible');
    requestAnimationFrame(() => {
      this.dropdownEl.classList.remove('opacity-0', 'scale-95');
      this.dropdownEl.classList.add('opacity-100', 'scale-100');
    });

    // outside + relayout
    this._setupOutside();
    this._attachObservers();

    this.isDropdownOpen = true;
  }

  close() {
    if (!this.isDropdownOpen) return;

    this.dropdownEl.classList.add('opacity-0', 'scale-95');
    this.dropdownEl.classList.remove('opacity-100', 'scale-100');

    const onEnd = () => {
      this.dropdownEl.classList.add('hidden');
      this.dropdownEl.removeEventListener('transitionend', onEnd);
      // Move dropdown back to original parent so Stimulus targets work
      this.dropdownOriginParent.appendChild(this.dropdownEl);
    };
    this.dropdownEl.addEventListener('transitionend', onEnd);

    this.buttonTarget.setAttribute('aria-expanded', 'false');

    this._unbindDropdownHandlers();
    this._teardownOutside();
    this._detachObservers();

    this.isDropdownOpen = false;
  }

  // === Selection helpers ===
  get selectedInputs() {
    if (!this.dropdownEl) return [];

    return this.dropdownEl.querySelectorAll(
      'input[type="checkbox"]:checked, input[type="radio"]:checked',
    );
  }
  get selectedValues() {
    return Array.from(this.selectedInputs).map((i) => i.value);
  }

  get selectedOptions() {
    return Array.from(this.selectedInputs).map((input) => {
      const value = input.value;
      const meta = this._optionMeta(value);
      return {
        value: value,
        label: meta.label,
        name: meta.label,
        avatar_url: meta.avatarUrl,
        color: input.dataset.color || '',
        text_color: input.dataset.textColor || '',
        firm_id: input.dataset.firmId || '',
        firm_name: input.dataset.firmName || ''
      };
    });
  }

  selectFromItem(event) {
    const li = event.currentTarget?.closest?.('li[role="option"]') || event.currentTarget;
    const input = li?.querySelector('input[type="checkbox"], input[type="radio"]');
    if (!input) return;
    input.type === 'checkbox' ? (input.checked = !input.checked) : (input.checked = true);
    this._onSelectionChanged();
    event.preventDefault();
    event.stopPropagation();
  }
  toggleOption() {
    this._onSelectionChanged();
  }

  _onSelectionChanged() {
    this._renderChipsFromDOM();
    this._enforceMaxSelections();
    this.clearError();
    // Dispatch change event to notify parent controllers (e.g., completion-quote-form)
    this.element.dispatchEvent(new Event('change', { bubbles: true }));
    if (this.hasAutoSubmitValue && this.autoSubmitValue) {
      this.submitForm();
    }
  }

  showError() {
    if (this.buttonTarget) {
      this.buttonTarget.classList.add('border-red-300');
      this.buttonTarget.classList.remove('border-black-100');
    }

    this.element.classList.add('has-error');
  }

  showRequiredError() {
    this.showError();

    if (!this.hasHelperTarget) {
      const helperText = document.createElement('p');
      helperText.dataset.multiSelectChipsWithSearchTarget = 'helper';
      helperText.className = 'mt-8 text-paragraph-s text-red-1000';
      const label = this.element.querySelector('label');
      const fieldName = label ? label.textContent.trim().replace(/\s*\*\s*$/, '') : 'Field';
      helperText.textContent = `${fieldName} can't be blank`;
      this.element.appendChild(helperText);
    } else {
      this.helperTarget.style.display = '';
      this.helperTarget.classList.add('text-red-1000');
    }
  }

  clearError() {
    if (this.buttonTarget) {
      this.buttonTarget.classList.remove('border-red-300');
      this.buttonTarget.classList.add('border-black-100');
    }

    this.element.classList.remove('has-error');

    if (this.hasHelperTarget && this.helperTarget.dataset.backendError !== 'true') {
      this.helperTarget.style.display = 'none';
    }
  }

  clearSelection() {
    const inputs = this.dropdownEl.querySelectorAll('input[type="checkbox"], input[type="radio"]');
    inputs.forEach((i) => {
      if (i.disabled || i.dataset.locked === 'true') return;
      i.checked = false;
    });
    this._onSelectionChanged();
  }

  preselectByIds(selectedIds) {
    if (!selectedIds || !Array.isArray(selectedIds)) return;

    const inputs = this.dropdownEl.querySelectorAll('input[type="checkbox"], input[type="radio"]');
    inputs.forEach((input) => {
      input.checked = selectedIds.includes(String(input.value));
    });
    this._renderChipsFromDOM();
  }

  // === Chips rendering ===
  _renderChipsFromDOM() {
    const btn = this.buttonTarget;
    btn.innerHTML = '';

    const values = this.selectedValues;
    const ph = this.buttonTarget.dataset.placeholder || '';

    if (!values.length) {
      btn.innerHTML = `<span class="text-black-400">${ph}</span>`;
      btn.classList.remove('text-black-1000');
      btn.classList.add('text-black-400');
      return;
    }

    btn.classList.add('text-black-1000');
    btn.classList.remove('text-black-400');

    const maxVisible = this.hasMaxVisibleChipsValue ? this.maxVisibleChipsValue : null;
    const hasOverflow = maxVisible && values.length > maxVisible;
    const visibleValues = hasOverflow ? values.slice(0, maxVisible) : values;

    visibleValues.forEach((value) => {
      const { label, avatarUrl, chipClasses, shapeCls, sizeCls, color, textColor } = this._optionMeta(value);

      const chip = document.createElement('span');
      // Remove bg-transparent and text-color classes if we have a color, so inline styles work
      let wrapperClass = chipClasses.wrapper;
      if (color) {
        wrapperClass = wrapperClass.replace('bg-transparent', '').trim();
      }
      if (textColor) {
        // Remove any text-black-* or text-* color classes
        wrapperClass = wrapperClass.replace(/text-black-\d+/g, '').trim();
      }
      chip.className = wrapperClass;
      chip.dataset.value = value;

      // Apply color styles if they exist (for label chips)
      if (color) {
        chip.style.backgroundColor = color;
        chip.style.borderColor = color;
      }
      if (textColor) {
        chip.style.color = textColor;
      }

      const initials =
        (label || '')
          .split(' ')
          .map((w) => w[0]?.toUpperCase())
          .join('') || '?';

      const avatarHTML = this._showAvatars()
        ? avatarUrl
          ? `<div class="${[sizeCls, shapeCls, 'overflow-hidden border border-black-400', chipClasses.avatar].join(' ')}">
                 <img src="${avatarUrl}" alt="${label}" class="w-full h-full object-cover" />
               </div>`
          : `<div class="${[sizeCls, shapeCls, 'bg-black-50 border border-black-400 flex items-center justify-center text-black-400 font-medium', chipClasses.avatar].join(' ')}">
                 <span class="text-[11px]">${initials}</span>
               </div>`
        : '';

      // For colored chips, adjust the remove button to inherit the text color
      const removeButtonStyle = textColor ? `style="color: ${textColor};"` : '';

      const removeButton = this._isLocked(value)
        ? ''
        : `<button type="button" class="${chipClasses.remove}" ${removeButtonStyle} data-value="${value}" data-action="click->multi-select-chips-with-search#removeChip">
          ${this._closeIcon()}
        </button>`;

      chip.innerHTML = `
        ${avatarHTML}
        <span class="${chipClasses.label}">${label}</span>
        ${removeButton}
      `;
      btn.appendChild(chip);
    });

    // Add "+X" chip if there are more items
    if (hasOverflow) {
      const remainingCount = values.length - maxVisible;
      const overflowChip = document.createElement('span');
      // Use special styling for overflow chip
      const defaultWrapper = this.wrapperClassValue || 'min-h-32 border border-black-100 pl-4 pr-6 bg-transparent rounded-[20px] inline-flex items-center gap-6 text-black-1000 text-paragraph-s';
      overflowChip.className = defaultWrapper.replace('border-black-100', 'border-brand-100').replace('text-black-1000', 'text-brand-1000');
      overflowChip.dataset.overflow = 'true';

      const labelClass = this.labelClassValue || '';
      const labelSpan = document.createElement('span');
      labelSpan.className = labelClass;
      labelSpan.textContent = `+${remainingCount}`;
      overflowChip.appendChild(labelSpan);

      btn.appendChild(overflowChip);
    }
  }

  removeChip(event) {
    event.preventDefault();
    event.stopPropagation();
    const value = event.currentTarget?.dataset?.value;
    if (!value) return;
    if (this._isLocked(value)) return;

    const cb = this.dropdownEl.querySelector(
      `input[type="checkbox"][value="${CSS.escape(value)}"], input[type="radio"][value="${CSS.escape(value)}"]`,
    );
    if (cb) cb.checked = false;

    this._onSelectionChanged();
  }

  _isLocked(value) {
    const input = this.dropdownEl?.querySelector(`input[value="${CSS.escape(value)}"]`);
    return !!(input && (input.disabled || input.dataset.locked === 'true'));
  }

  _optionMeta(value) {
    const input = this.dropdownEl.querySelector(`input[value="${CSS.escape(value)}"]`);
    const li = input?.closest('li[role="option"][data-value]');
    const labelEl =
      li?.querySelector('[data-label]') ||
      input?.closest('label')?.querySelector('.text-paragraph-m, span');
    const label = input?.dataset.label || input?.dataset.name || labelEl?.textContent?.trim() || input?.value || value;

    const wrapper =
      (input?.dataset.chipWrapperClass || '').trim() ||
      this.wrapperClassValue ||
      'border border-black-100 pl-6 py-4 pr-6 bg-transparent rounded-[20px] flex items-center gap-6 text-black-1000 text-paragraph-s';
    const avatar = (input?.dataset.chipAvatarClass || '').trim() || this.avatarClassValue || '';
    const labelC = (input?.dataset.chipLabelClass || '').trim() || this.labelClassValue || '';

    // Extract color and text_color for label chips
    const color = (input?.dataset.color || '').trim();
    const textColor = (input?.dataset.textColor || '').trim();

    // For colored chips, don't use default text color class - rely on inline style
    let remove =
      (input?.dataset.chipRemoveClass || '').trim() ||
      this.removeClassValue ||
      '!p-0 text-black-400 w-16 h-16 cursor-pointer';

    // Remove text-black-* classes from remove button if we have textColor
    if (textColor) {
      remove = remove.replace(/text-black-\d+/g, '').trim();
    }

    const sizeCls = this.avatarSizeClassValue || 'w-20 h-20';
    const shape = (input?.dataset.avatarShape || 'circle').trim();
    const shapeCls = shape === 'square' ? 'rounded-6' : 'rounded-full';

    const avatarUrl = (input?.dataset.avatarUrl || '').trim() || null;

    return {
      label,
      avatarUrl,
      sizeCls,
      shapeCls,
      chipClasses: { wrapper, avatar, label: labelC, remove },
      color,
      textColor,
    };
  }

  _showAvatars() {
    return this.hasShowAvatarsValue ? this.showAvatarsValue : true;
  }

  // === Search & filter ===
  _grabRefs() {
    this._searchEl = this.hasSearchTarget
      ? this.searchTarget
      : this.dropdownEl.querySelector('[data-multi-select-chips-with-search-target="search"]');
    this._listEl = this.hasListTarget
      ? this.listTarget
      : this.dropdownEl.querySelector('[data-multi-select-chips-with-search-target="list"]');
    this._clearBtnEl = this.hasClearSearchBtnTarget
      ? this.clearSearchBtnTarget
      : this.dropdownEl.querySelector(
          '[data-multi-select-chips-with-search-target="clearSearchBtn"]',
        );
    this._emptyEl = this.dropdownEl.querySelector('[data-ss-empty]');
  }

  _normalize(str) {
    return (str || '')
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  _filter(q) {
    const list = this._listEl || this.dropdownEl;
    const items = list.querySelectorAll('li[role="option"][data-value]');
    const qq = this._normalize(q);
    let visible = 0;

    items.forEach((li) => {
      const attr = li.getAttribute('data-label') || '';
      const node = li.querySelector('[data-label]') || li;
      const raw = attr || (node ? node.textContent : '') || '';
      const match = !qq || this._normalize(raw).includes(qq);
      li.hidden = !match;
      if (match) visible++;
    });

    list.querySelectorAll('[data-group]').forEach((groupLi) => {
      const childVisible = groupLi.querySelector('li[role="option"][data-value]:not([hidden])');
      groupLi.hidden = !childVisible;
      const header = groupLi.previousElementSibling;
      if (header && header.matches('[data-group-header]')) header.hidden = groupLi.hidden;
    });

    if (this._emptyEl) this._emptyEl.hidden = !(qq && visible === 0);
  }

  _toggleClearBtn(show) {
    if (this._clearBtnEl) this._clearBtnEl.classList.toggle('hidden', !show);
  }

  // === Teleport bindings ===
  _bindDropdownHandlers() {
    if (this._dropdownHandlersBound || !this.dropdownEl) return;

    this._grabRefs();

    this._onDropdownChange = (e) => {
      const input = e.target?.closest?.('input[type="checkbox"], input[type="radio"]');
      if (input && this.dropdownEl.contains(input)) {
        this._onSelectionChanged();
      }
    };

    this._onDropdownClick = (e) => {
      const inputEl = e.target?.closest?.('input[type="checkbox"], input[type="radio"]');
      if (inputEl) {
        requestAnimationFrame(() => this._onSelectionChanged());
        e.stopPropagation();
        return;
      }

      const labelEl = e.target?.closest?.('label[for]');
      if (labelEl) {
        requestAnimationFrame(() => this._onSelectionChanged());
        return;
      }
      const li = e.target?.closest?.('li[role="option"]');
      if (li && this.dropdownEl.contains(li)) {
        const lbl = li.querySelector('label[for]');
        if (lbl) lbl.click();
        else {
          const i = li.querySelector('input[type="checkbox"], input[type="radio"]');
          if (i) {
            i.checked = i.type === 'checkbox' ? !i.checked : true;
            this._onSelectionChanged();
          }
        }
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      // Toolbar (select visible / clear)
      const tb = e.target?.closest?.('[data-ms-action]');
      if (tb && this.dropdownEl.contains(tb)) {
        const act = tb.getAttribute('data-ms-action');
        if (act === 'select-visible') this._selectAllVisible();
        if (act === 'clear-selection') this.clearSelection();
        e.preventDefault();
        e.stopPropagation();
      }
    };

    this.dropdownEl.addEventListener('change', this._onDropdownChange);
    this.dropdownEl.addEventListener('click', this._onDropdownClick);

    // search
    this._onSearchInput = () => {
      const q = this._searchEl ? this._searchEl.value : '';
      this._toggleClearBtn(!!q);
      this._filter(q);
      this._positionDropdown();
    };
    this._onSearchKeydown = (e) => {
      if (e.key === 'Escape') {
        if (this._searchEl?.value) {
          this._searchEl.value = '';
          this._toggleClearBtn(false);
          this._filter('');
          this._positionDropdown();
        } else {
          this.close();
        }
        e.preventDefault();
      }
    };
    if (this._searchEl) {
      this._searchEl.addEventListener('input', this._onSearchInput);
      this._searchEl.addEventListener('keydown', this._onSearchKeydown);
    }
    if (this._clearBtnEl) {
      this._onClearClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!this._searchEl) return;
        this._searchEl.value = '';
        this._toggleClearBtn(false);
        this._filter('');
        try {
          this._searchEl.focus({ preventScroll: true });
        } catch (_) {}
        this._positionDropdown();
      };
      this._clearBtnEl.addEventListener('click', this._onClearClick);
    }

    this._dropdownHandlersBound = true;
  }

  _unbindDropdownHandlers() {
    if (!this._dropdownHandlersBound || !this.dropdownEl) return;
    this.dropdownEl.removeEventListener('change', this._onDropdownChange);
    this.dropdownEl.removeEventListener('click', this._onDropdownClick);
    if (this._searchEl) {
      this._searchEl.removeEventListener('input', this._onSearchInput);
      this._searchEl.removeEventListener('keydown', this._onSearchKeydown);
    }
    if (this._clearBtnEl) {
      this._clearBtnEl.removeEventListener('click', this._onClearClick);
    }
    this._onDropdownChange = null;
    this._onDropdownClick = null;
    this._onSearchInput = null;
    this._onSearchKeydown = null;
    this._onClearClick = null;
    this._dropdownHandlersBound = false;
  }

  // === Portal & positioning ===
  _ensurePortalRoot() {
    let root = document.getElementById('ui-layer-root');
    if (!root) {
      root = document.createElement('div');
      root.id = 'ui-layer-root';
      root.classList.add('ui-layer-root');
      document.body.appendChild(root);
    }
    return root;
  }

  _positionDropdown() {
    const btnRect = this.buttonTarget.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const M = 8;

    const minW = this.hasMinWidthValue ? this.minWidthValue : 200;
    const maxW = this.hasMaxWidthValue ? this.maxWidthValue : Infinity;
    const width = Math.max(minW, Math.min(btnRect.width, maxW));

    // Temporarily show all items and remove max-height to measure natural height
    const list = this._listEl || this.dropdownEl;
    const items = list.querySelectorAll('li[role="option"][data-value]');
    const hiddenStates = [];
    items.forEach((li, idx) => {
      hiddenStates[idx] = li.hidden;
      li.hidden = false;
    });

    this.dropdownEl.classList.add('ss-no-max-h');
    const naturalH = this.dropdownEl.offsetHeight || 300;
    this.dropdownEl.classList.remove('ss-no-max-h');

    // Restore hidden states
    items.forEach((li, idx) => {
      li.hidden = hiddenStates[idx];
    });

    const spaceBelow = vh - btnRect.bottom - M;
    const spaceAbove = btnRect.top - M;
    const wantsTop = this.directionValue === 'top';
    const wantsBottom = this.directionValue === 'bottom';
    const openUp = wantsTop
      ? true
      : wantsBottom
        ? false
        : spaceBelow < Math.min(naturalH, 320) && spaceAbove > spaceBelow;

    let top = openUp ? Math.max(M, btnRect.top - Math.min(naturalH, spaceAbove)) : btnRect.bottom;
    const maxH = openUp ? spaceAbove : spaceBelow;

    let left = btnRect.left;
    if (left + width + M > vw) left = Math.max(M, vw - width - M);
    if (left < M) left = M;

    this._setVar('--ss-left', `${Math.round(left)}px`);
    this._setVar('--ss-top', `${Math.round(top)}px`);
    this._setVar('--ss-width', `${Math.round(width)}px`);
    this._setVar('--ss-max-h', `${dropdownMaxHeightPx(naturalH, maxH)}px`);
  }

  _setVar(name, value) {
    this.dropdownEl.style.setProperty(name, value);
  }

  _attachObservers() {
    this._onScroll = (e) => {
      if (this.dropdownEl && this.dropdownEl.contains(e.target)) return;
      this._reposition();
    };
    window.addEventListener('scroll', this._onScroll, true);
    window.addEventListener('resize', this._reposition);
    this.ro = new ResizeObserver(this._reposition);
    this.ro.observe(this.buttonTarget);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this._reposition);
      window.visualViewport.addEventListener('scroll', this._onScroll);
    }
  }

  _detachObservers() {
    window.removeEventListener('scroll', this._onScroll, true);
    window.removeEventListener('resize', this._reposition);
    this.ro?.disconnect();
    this.ro = undefined;
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this._reposition);
      window.visualViewport.removeEventListener('scroll', this._onScroll);
    }
  }

  _setupOutside() {
    if (this.outsideBound) return;
    document.addEventListener('pointerdown', this._onDocPointerDown, true);
    document.addEventListener('keydown', this._onDocKeydown);
    this.outsideBound = true;
  }

  _teardownOutside() {
    if (!this.outsideBound) return;
    document.removeEventListener('pointerdown', this._onDocPointerDown, true);
    document.removeEventListener('keydown', this._onDocKeydown);
    this.outsideBound = false;
  }

  // === API for dynamic data refresh ===
  refreshOptions(data) {
    // Save current selections (values that are checked)
    const currentlySelected = this.selectedValues;

    // Clear the list
    const list = this._listEl || this.dropdownEl?.querySelector('[data-multi-select-chips-with-search-target="list"]');
    if (!list) return;

    // Save field name and form_id BEFORE removing options
    // Use Stimulus values as fallback when no existing inputs
    const fieldName = this._getFieldName();
    const existingInput = this.dropdownEl?.querySelector('input[type="checkbox"][name]');
    const formId = existingInput?.getAttribute('form') || this.formIdValue;

    // Remove all existing option items (but keep the empty state message)
    const existingOptions = list.querySelectorAll('li[role="option"]');
    existingOptions.forEach(li => li.remove());

    // Build new options
    data.forEach((option, index) => {
      const optionId = String(option.id || option.value);
      const shouldBeChecked = currentlySelected.includes(optionId);

      const li = this._buildOptionElement(option, index, shouldBeChecked, fieldName, formId);
      const emptyMsg = list.querySelector('[data-ss-empty]');
      if (emptyMsg) {
        list.insertBefore(li, emptyMsg);
      } else {
        list.appendChild(li);
      }
    });

    this._renderChipsFromDOM();
  }

  _buildOptionElement(option, index, shouldBeChecked = false, fieldName = null, formId = null) {
    const li = document.createElement('li');
    li.className = '!p-10 !w-full cursor-pointer';
    li.setAttribute('role', 'option');
    li.setAttribute('aria-selected', shouldBeChecked ? 'true' : 'false');
    li.setAttribute('data-value', option.id || option.value);
    li.setAttribute('data-label', option.name || option.label);

    const optionId = `${this.buttonTarget.id}_${index}_${String(option.id || option.value || '').replace(/[^a-z0-9]/gi, '-')}`;

    const label = document.createElement('label');
    label.setAttribute('for', optionId);
    label.className = 'flex items-center gap-8 w-full cursor-pointer';

    // Checkbox container
    const checkboxContainer = document.createElement('div');
    checkboxContainer.className = 'relative box-border w-20 h-20';

    // Checkbox input
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.id = optionId;
    input.name = fieldName || this._getFieldName();
    input.value = option.id || option.value;
    input.checked = shouldBeChecked;

    // Set form attribute if formId is provided
    if (formId) {
      input.setAttribute('form', formId);
    }

    input.dataset.value = option.id || option.value;
    input.dataset.name = option.name || option.label;
    input.dataset.label = option.name || option.label;
    if (option.avatar_url) input.dataset.avatarUrl = option.avatar_url;
    if (option.color) input.dataset.color = option.color;
    if (option.text_color) input.dataset.textColor = option.text_color;
    if (option.firm_id) input.dataset.firmId = option.firm_id;
    if (option.firm_name) input.dataset.firmName = option.firm_name;
    input.className = `peer box-border shrink-0 w-20 h-20 rounded-sm border border-black-200 appearance-none cursor-pointer
                       checked:bg-black-1000 checked:border-black-1000
                       focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black-100
                       hover:border-black-300 transition`;

    // Checkmark icon
    const checkmark = document.createElement('div');
    checkmark.className = 'pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white text-[8px] hidden peer-checked:block';
    checkmark.setAttribute('aria-hidden', 'true');
    checkmark.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-full h-full"><path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd"/></svg>`;

    checkboxContainer.appendChild(input);
    checkboxContainer.appendChild(checkmark);

    // Label text
    const labelText = document.createElement('span');
    labelText.className = 'text-paragraph-m text-black-1000';
    labelText.setAttribute('data-label', '');
    labelText.textContent = option.name || option.label;

    label.appendChild(checkboxContainer);
    label.appendChild(labelText);
    li.appendChild(label);

    return li;
  }

  _getFieldName() {
    // Try to get field name from existing inputs, then Stimulus value, then default
    const existingInput = this.dropdownEl?.querySelector('input[type="checkbox"][name]');
    return existingInput?.name || this.fieldNameValue || 'options[]';
  }

  // === misc ===
  submitForm() {
    const form = this.hasFormIdValue
      ? document.getElementById(this.formIdValue)
      : this.element.closest('form');
    if (form) form.requestSubmit();
  }

  _enforceMaxSelections() {
    if (!this.hasMaxSelectionsValue || !this.maxSelectionsValue) return;

    const atLimit = this.selectedValues.length >= this.maxSelectionsValue;
    const allCheckboxes = this.dropdownEl.querySelectorAll('input[type="checkbox"]');

    allCheckboxes.forEach((checkbox) => {
      if (checkbox.checked) return;
      checkbox.disabled = atLimit;
      const li = checkbox.closest('li[role="option"]');
      if (li) li.classList.toggle('opacity-40', atLimit);
    });

    if (this.hasLimitErrorTarget) {
      this.limitErrorTarget.classList.toggle('hidden', !atLimit);
    }
  }

  _selectAllVisible() {
    const max = this.hasMaxSelectionsValue ? this.maxSelectionsValue : null;
    const list = this._listEl || this.dropdownEl;
    const visibleLis = Array.from(list.querySelectorAll('li[role="option"][data-value]')).filter(
      (li) => !li.hidden && !li.closest('[hidden]'),
    );
    let selectionCount = this.selectedValues.length;
    visibleLis.forEach((li) => {
      const input = li.querySelector('input[type="checkbox"]');
      if (!input || input.checked) return;
      if (max && selectionCount >= max) return;
      input.checked = true;
      selectionCount++;
    });
    this._onSelectionChanged();
  }

   _closeIcon() {
    const v = (this.hasCloseIconValue ? this.closeIconValue : 'outline').toLowerCase();
      if (v === 'filled') {
        // filled
        return `
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"
              viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path opacity="0.1"
                  d="M22 12C22 17.523 17.523 22 12 22C6.477 22 2 17.523 2 12C2 6.477 6.477 2 12 2C17.523 2 22 6.477 22 12Z"
                  fill="currentColor"/>
            <path d="M15.2626 8.7373L8.7373 15.2626M8.7373 8.7373L15.2626 15.2626"
                  stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        `;
      }
      // outline (default)
      return `
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none"
           viewBox="0 0 24 24" aria-hidden="true">
          <g clip-path="url(#c)">
            <path d="M6.686 5.98a.5.5 0 0 0-.35.866l.957.957-.957.957a.5.5 0 1 0 .707.707l.957-.957.957.957a.5.5 0 1 0 .707-.707L8.707 7.803l.957-.957A.5.5 0 1 0 8.957 6.14l-.957.957-.957-.957a.5.5 0 0 0-.357-.16Z" fill="currentColor" opacity=".9"/>
            <path fill-rule="evenodd" clip-rule="evenodd"
                  d="M8 0.832A7.167 7.167 0 1 0 8 15.165 7.167 7.167 0 0 0 8 .832ZM1.834 8a6.167 6.167 0 1 1 12.333 0A6.167 6.167 0 0 1 1.834 8Z"
                  fill="currentColor" opacity=".9"/>
          </g>
          <defs><clipPath id="c"><rect width="16" height="16" fill="#fff"/></clipPath></defs>
        </svg>
      `;
   }
}
